/* ===========================================================================
   TradePlatform — Design System
   Dark-first enterprise UI. Tokens are Tailwind v4 @theme variables, so every
   token is available both as a utility (bg-surface, text-fg-muted) and as a
   CSS variable (var(--color-surface)) inside plain CSS.
   =========================================================================== */

@import "tailwindcss";

@theme {
  /* ── Typography ──────────────────────────────────────────────────────── */
  --font-sans: "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
  --font-mono: "JetBrains Mono", ui-monospace, "SFMono-Regular", Menlo, monospace;

  /* ── Surfaces ────────────────────────────────────────────────────────── */
  --color-canvas: #08090a;      /* page background */
  --color-surface: #0e1013;     /* cards, sidebar */
  --color-surface-2: #15181c;   /* hover, inputs, table headers */
  --color-surface-3: #1c2025;   /* popovers, active rows */

  /* ── Lines ───────────────────────────────────────────────────────────── */
  --color-line: #202429;        /* default separators */
  --color-line-strong: #2d3238; /* interactive borders */
  --color-line-hover: #3a4047;  /* interactive border, hovered */

  /* ── Foreground ──────────────────────────────────────────────────────── */
  --color-fg: #f2f3f5;          /* primary text */
  --color-fg-muted: #a4a9b0;    /* secondary text — 8.9:1 on canvas */
  --color-fg-subtle: #7d828a;   /* meta text — 4.6:1 on canvas */
  --color-fg-faint: #5b6069;    /* decorative only, never for values */

  /* ── Brand ───────────────────────────────────────────────────────────── */
  --color-brand: #6d5ee6;       /* solid: primary buttons, active indicator */
  --color-brand-hover: #7f72ec;
  --color-brand-active: #5b4cd6;
  --color-brand-fg: #a99cff;    /* brand text/icons on dark — 8.4:1 */
  --color-ring: #8b7bf7;

  /* ── Semantic ────────────────────────────────────────────────────────── */
  --color-success: #3fb950;
  --color-danger: #f85149;
  --color-warning: #d29922;
  --color-info: #58a6ff;

  /* ── Trading ─────────────────────────────────────────────────────────── */
  --color-profit: #3fb950;
  --color-loss: #f85149;
  --color-buy: #58a6ff;
  --color-sell: #f85149;

  /* ── Elevation (custom names; Tailwind's defaults stay intact) ───────── */
  --shadow-raised: 0 1px 2px 0 rgb(0 0 0 / 0.4);
  --shadow-popover: 0 8px 24px -6px rgb(0 0 0 / 0.55), 0 0 0 1px rgb(255 255 255 / 0.04);
  --shadow-modal: 0 24px 64px -16px rgb(0 0 0 / 0.7);
}

/* ── Base ─────────────────────────────────────────────────────────────── */

@layer base {
  html {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color-scheme: dark;
  }

  body {
    background-color: var(--color-canvas);
    color: var(--color-fg);
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1.5;
  }

  #root {
    min-height: 100vh;
  }

  :focus-visible {
    outline: 2px solid var(--color-ring);
    outline-offset: 2px;
    border-radius: 4px;
  }

  ::selection {
    background-color: color-mix(in oklab, var(--color-brand) 45%, transparent);
    color: var(--color-fg);
  }

  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: var(--color-line-strong);
    border: 3px solid var(--color-canvas);
    border-radius: 999px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: var(--color-line-hover);
  }

  /* Native form controls inherit the dark palette */
  input,
  select,
  textarea,
  button {
    font: inherit;
    color: inherit;
  }
}

/* ── Utilities ────────────────────────────────────────────────────────── */

@utility font-tabular {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

@utility no-scrollbar {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

.animate-fade-in {
  animation: fade-in 160ms cubic-bezier(0.2, 0, 0, 1);
}

.brand-wash {
  background: radial-gradient(
    120% 80% at 12% 0%,
    color-mix(in oklab, var(--color-brand) 14%, transparent) 0%,
    transparent 60%
  );
}

.skeleton {
  background-image: linear-gradient(
    90deg,
    var(--color-surface-2) 25%,
    var(--color-surface-3) 50%,
    var(--color-surface-2) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;
  border-radius: 6px;
}

/* ── Reduced motion ───────────────────────────────────────────────────── */

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
